/**
 * Safe parsers for the pricing-adjustment audit activity metadata
 * (work_order_adjustment.applied/.removed and adjustment_rule.*).
 *
 * Activity metadata is loosely-typed JSON and old rows may miss fields —
 * these parsers validate the essentials against the shared
 * workOrderAdjustment types and return null when a meaningful render is
 * impossible, so the detail components can fall back to the activity's
 * plain description text instead of crashing.
 */
const str = (v) => (typeof v === 'string' && v !== '' ? v : undefined);
function asAdjustmentType(v) {
    return v === 'discount' || v === 'surcharge' ? v : undefined;
}
function asMethod(v) {
    return v === 'percent' || v === 'fixed' ? v : undefined;
}
export function parseAdjustmentEventMetadata(metadata) {
    if (!metadata || typeof metadata !== 'object')
        return null;
    const adjustmentType = asAdjustmentType(metadata.adjustment_type);
    const amount = str(metadata.amount) ?? (typeof metadata.amount === 'number' ? String(metadata.amount) : undefined);
    // Type + amount are the essentials — without them there is nothing better
    // to show than the description text.
    if (!adjustmentType || amount === undefined || !Number.isFinite(Number(amount)))
        return null;
    return {
        adjustmentType,
        method: asMethod(metadata.method),
        value: str(metadata.value) ?? (typeof metadata.value === 'number' ? String(metadata.value) : undefined),
        amount,
        reason: str(metadata.reason),
        sourceKind: metadata.source_kind === 'rule' ? 'rule' : 'manual',
        ruleId: str(metadata.rule_id),
        ruleName: str(metadata.rule_name),
        removalReason: str(metadata.removal_reason),
        actedBy: str(metadata.acted_by),
        actedAt: str(metadata.acted_at),
    };
}
export function parseAdjustmentRuleMetadata(metadata) {
    if (!metadata || typeof metadata !== 'object')
        return null;
    const name = str(metadata.name);
    if (!name)
        return null;
    return {
        ruleId: str(metadata.rule_id),
        name,
        adjustmentType: asAdjustmentType(metadata.adjustment_type),
        method: asMethod(metadata.method),
        value: str(metadata.value) ?? (typeof metadata.value === 'number' ? String(metadata.value) : undefined),
        autoApply: typeof metadata.auto_apply === 'boolean' ? metadata.auto_apply : undefined,
        locationId: str(metadata.location_id),
    };
}
